/**
 * One review row on the public itinerary view (U6). Scalar 1–5 rating (D2 —
 * there is no subscore breakdown here, unlike a venue review), optional body,
 * author handle and relative time.
 *
 * The author of the row gets Edit / Delete; every other authed viewer gets
 * Flag. An anonymous viewer gets neither — the row is read-only.
 */

import { StarDisplay } from '../reviews/ReviewRatingDisplays';
import { BodyText, Caption } from '../ui/Typography';
import { Button } from '../ui/Button';
import type { ItineraryReviewItem } from './itineraryTypes';
import { formatRelativeTime } from './reviewTime';

interface ItineraryReviewRowProps {
  review: ItineraryReviewItem;
  /** The signed-in viewer wrote this review. */
  isOwn: boolean;
  /** The viewer may flag this row (authed, not the author). */
  canFlag: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onFlag: () => void;
  /** A mutation is in flight — freeze the row's actions. */
  actionsDisabled?: boolean;
}

export const ItineraryReviewRow = ({
  review,
  isOwn,
  canFlag,
  onEdit,
  onDelete,
  onFlag,
  actionsDisabled = false,
}: ItineraryReviewRowProps) => {
  // No superjson transformer on this client (see `lib/trpc.ts`), so the
  // timestamps typed as Date arrive as ISO strings. Both the comparison and
  // `formatRelativeTime` are written to be correct either way.
  const edited = review.updatedAt > review.createdAt;

  return (
    <div className="border-b border-[#DBD4C9] py-4 last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <StarDisplay value={review.rating} size="sm" />
          <Caption className="mt-1 block text-gray-600">
            {isOwn ? 'You' : review.authorHandle} &middot; {formatRelativeTime(review.createdAt)}
            {edited ? ' · edited' : ''}
          </Caption>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          {isOwn && (
            <>
              <Button variant="outline" size="sm" onClick={onEdit} disabled={actionsDisabled}>
                Edit
              </Button>
              <Button variant="ghost" size="sm" onClick={onDelete} disabled={actionsDisabled}>
                Delete
              </Button>
            </>
          )}
          {canFlag && (
            <Button variant="ghost" size="sm" onClick={onFlag} disabled={actionsDisabled}>
              Report
            </Button>
          )}
        </div>
      </div>

      {review.body ? (
        <BodyText className="mt-2 whitespace-pre-wrap text-gray-700">{review.body}</BodyText>
      ) : null}

      {/* A flagged review only reaches its own author or a moderator; say so
          rather than letting it look like an ordinary published row. */}
      {review.status === 'flagged' && (
        <Caption className="mt-2 block text-amber-700">
          This review has been reported and is awaiting moderation.
        </Caption>
      )}
    </div>
  );
};
